import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import MedicalRecordsSection from "../components/medical-records/medical-records-section";
import ProviderSummaryCard from "../components/providers/provider-summary-card";
import ThemedSpinner from "../components/shared/spinner";
import Typography from "../components/shared/typography";
import { ActionButton } from "../components/shared/buttons/ActionButtons";
import { useSetPageContext } from "../hooks/PageContext";
import useLoadProviders from "../hooks/useLoadProviders";
import { buildRecordDetailPath } from "#/lib/medicalRecords";
import { setPatientProviderPrimary } from "../api/patientProviders.api";
import { useUserDetails } from "../hooks/useUserDetails";
import { useProviderPatientRequests } from "../hooks/useProviderPatientRequests";

const ProvidersPage = () => {
  const { userId } = useParams<{ userId?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { userDetails } = useUserDetails();
  const resolvedUserId = userId || userDetails?.id;
  const { providers, isLoading, error } = useLoadProviders(userId);
  useSetPageContext(providers);

  const canManageOwnRequests = !userId || userId === userDetails?.id;
  const {
    query: {
      data: incomingRequests = [],
      isLoading: loadingRequests,
      error: requestsError,
    },
    respond: {
      mutateAsync: respondRequest,
      isPending: isRespondingRequest,
    },
  } = useProviderPatientRequests({
    enabled: Boolean(resolvedUserId) && canManageOwnRequests,
    keyScope: resolvedUserId || "self",
  });

  const { mutateAsync: makePrimary, isPending: isSettingPrimary } = useMutation({
    mutationFn: async (patientProviderId: string) =>
      setPatientProviderPrimary(patientProviderId, {
        userId,
        isPrimary: true,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["providers", resolvedUserId] });
    },
  });



  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <ThemedSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Typography variant="h4" className="text-terracotta">
          Failed to load providers
        </Typography>
      </div>
    );
  }

  const hasProviders = providers.length > 0;

  return (
    <MedicalRecordsSection
      title="Providers"
      buttonLabel="Add Provider"
      uploadMessage={`Add provider to this user's medical records ${
        userId ?? ""
      }`}
      hasRecords={hasProviders}
      emptyTitle="No providers listed."
      emptyDescription="No primary physician set — we’ll route to the next available physician if needed."
    >
      {canManageOwnRequests && loadingRequests ? (
        <div className="mb-4 text-sm text-stone-600">Loading provider requests…</div>
      ) : canManageOwnRequests && requestsError ? (
        <div className="mb-4 text-sm text-red-600">Failed to load provider requests.</div>
      ) : canManageOwnRequests && incomingRequests.length > 0 ? (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <Typography variant="h5" className="mb-3 text-terracotta">
            Provider Requests
          </Typography>
          <div className="space-y-3">
            {incomingRequests.map((r) => (
              <div key={r.id} className="rounded-lg border border-amber-200 bg-white p-3">
                <div className="text-sm text-stone-800">
                  {r.message || "A provider would like to be listed as your provider."}
                </div>
                <div className="mt-2 flex gap-2">
                  <ActionButton
                    tone="primary"
                    size="sm"
                    isLoading={isRespondingRequest}
                    onClick={() =>
                      respondRequest({ id: r.id, action: "accept", makePrimary: true })
                    }
                  >
                    Accept (Set Primary)
                  </ActionButton>
                  <ActionButton
                    tone="secondary"
                    size="sm"
                    isLoading={isRespondingRequest}
                    onClick={() => respondRequest({ id: r.id, action: "accept", makePrimary: false })}
                  >
                    Accept
                  </ActionButton>
                  <ActionButton
                    tone="danger"
                    size="sm"
                    isLoading={isRespondingRequest}
                    onClick={() => respondRequest({ id: r.id, action: "decline" })}
                  >
                    Decline
                  </ActionButton>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {hasProviders
        ? providers.map((p) => (
            <div
              key={p.id}
              id={`record-provider-${p.id}`}
              className="file-record-anchor block scroll-mt-24"
            >
              <ProviderSummaryCard
                provider={p}
                onClick={() => {
                  const detailPath = buildRecordDetailPath(
                    "providers",
                    p.provider_id ?? p.id,
                    userId
                  );
                  if (!detailPath) return;
                  navigate(detailPath, { state: { record: p } });
                }}
              />
              {!p.is_primary && (
                <div className="mt-2">
                  <ActionButton
                    tone="secondary"
                    size="sm"
                    isLoading={isSettingPrimary}
                    onClick={() => makePrimary(p.id)}
                  >
                    Make Primary
                  </ActionButton>
                </div>
              )}
            </div>
          ))
        : null}
    </MedicalRecordsSection>
  );
};

export default ProvidersPage;
